import { IconMicrophone } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/common';
import { currentLanguage } from '@/i18n';
import type { Entry } from '@/types/api';
import { codeLabel, materialLabel } from '@/utils/dental';
import { deriveChart } from '@/utils/toothState';
import { Odontogram } from '../chart/components';
import { EMPTY_CONTEXT, type ParseContext, parseDictation } from '../dictation';
import { BASE_ENTRIES, draftEntry } from './demoData';

const TYPE_MS = 45;
const HOLD_MS = 1500;
const LOOP_PAUSE_MS = 3000;
/** Plays twice, then rests on the finished chart (no endless motion). */
const LOOPS = 2;

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Run every sentence through the real parser: the entries each one produces. */
function parseAll(sentences: string[]): Entry[][] {
  let ctx: ParseContext = EMPTY_CONTEXT;
  const lang = currentLanguage();
  return sentences.map((sentence, i) => {
    const r = parseDictation(sentence, ctx, { lang });
    ctx = r.context;
    // Stable ids, so a card keeps its place (and does not re-animate) on every render.
    return r.drafts.map((d, k) => ({
      ...draftEntry(d, `2026-10-22T09:0${i}:00Z`),
      id: `demo-${i}-${k}`,
    }));
  });
}

function entryLabel(e: Entry): string {
  const parts = [codeLabel(e.code)];
  if (e.surfaces.length) parts.push(e.surfaces.join(''));
  if (e.material) parts.push(materialLabel(e.material).toLowerCase());
  return parts.join(' · ');
}

/**
 * Hero demo: example sentences are "dictated" one by one and run through the
 * real parser; the chart fills in as each one is understood.
 */
export function LiveDemo() {
  const { t } = useTranslation('landing');
  // returnObjects gives a new array on every render: key the parse on the text.
  const raw = t('demo.sentences', { returnObjects: true });
  const joined = Array.isArray(raw) ? raw.join('\n') : '';
  const sentences = useMemo(() => (joined ? joined.split('\n') : []), [joined]);
  const results = useMemo(() => parseAll(sentences), [sentences]);
  const still = reducedMotion();
  const [index, setIndex] = useState(still ? sentences.length : 0);
  const [typed, setTyped] = useState(0);
  const [loop, setLoop] = useState(1);

  useEffect(() => {
    if (still) return;
    if (index >= sentences.length) {
      if (loop >= LOOPS) return;
      const t1 = window.setTimeout(() => {
        setIndex(0);
        setTyped(0);
        setLoop(loop + 1);
      }, LOOP_PAUSE_MS);
      return () => window.clearTimeout(t1);
    }
    const sentence = sentences[index];
    if (typed < sentence.length) {
      const t2 = window.setTimeout(() => setTyped(typed + 1), TYPE_MS);
      return () => window.clearTimeout(t2);
    }
    const t3 = window.setTimeout(() => {
      setIndex(index + 1);
      setTyped(0);
    }, HOLD_MS);
    return () => window.clearTimeout(t3);
  }, [still, index, typed, sentences, loop]);

  // A sentence counts once it is fully "said".
  const done = Math.min(
    index + (typed >= (sentences[index]?.length ?? 0) ? 1 : 0),
    sentences.length
  );
  const recorded = useMemo(() => results.slice(0, done).flat(), [results, done]);
  const chart = useMemo(() => deriveChart([...BASE_ENTRIES, ...recorded]), [recorded]);
  const recent = new Set((results[done - 1] ?? []).map((e) => e.tooth));
  const line =
    index < sentences.length ? sentences[index].slice(0, typed) : sentences[sentences.length - 1];

  return (
    <figure className="phone">
      <div className="phone__screen">
        <div className="phone__bar">
          <Logo size={20} />
        </div>
        <div className="phone__chart" inert>
          <Odontogram
            chart={chart}
            dentition="permanent"
            selected={null}
            recent={recent}
            onSelect={() => {}}
          />
        </div>
        <div className="phone__dictation" aria-live="off">
          <span className="phone__mic">
            <IconMicrophone size={16} />
          </span>
          <span className="phone__line">
            {line}
            {!still && index < sentences.length && <span className="phone__caret" />}
          </span>
        </div>
        <ul className="phone__entries">
          {recorded
            .slice(-3)
            .reverse()
            .map((e) => (
              <li key={e.id}>
                <b>{e.tooth}</b>
                <span className="phone__entry">{entryLabel(e)}</span>
                <small>{t('demo.pending')}</small>
              </li>
            ))}
        </ul>
      </div>
      <figcaption>{t('demo.label')}</figcaption>
    </figure>
  );
}
